import { type FormEvent, useState } from 'react';
import { Modal } from '../Modal';
import { slugify } from '../../lib/slug';

export interface TaxonomyEntity {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  isActive: boolean;
}

export interface TaxonomyFormValues {
  key: string;
  name: string;
  description?: string;
  isActive: boolean;
}

interface BlogTaxonomyModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: TaxonomyFormValues) => void;
  initial?: TaxonomyEntity | null;
  entityLabel: string;
  isPending: boolean;
}

export function BlogTaxonomyModal({
  open,
  onClose,
  onSubmit,
  initial,
  entityLabel,
  isPending,
}: BlogTaxonomyModalProps) {
  const isEdit = initial != null;
  const [name, setName] = useState(initial?.name ?? '');
  const [keyDraft, setKeyDraft] = useState(initial?.key ?? '');
  const [keyTouched, setKeyTouched] = useState(Boolean(initial?.key));
  const [description, setDescription] = useState(initial?.description ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const key = keyTouched ? keyDraft : slugify(name);

  const inputClass =
    'w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors';

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !key) return;
    onSubmit({
      key,
      name: name.trim(),
      description: description.trim() ? description.trim() : undefined,
      isActive,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${isEdit ? 'Edit' : 'Create'} ${entityLabel}`}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Key <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={key}
            onChange={(e) => {
              setKeyDraft(e.target.value);
              setKeyTouched(true);
            }}
            disabled={isEdit}
            placeholder="auto-generated-from-name"
            className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
          />
          {isEdit && (
            <p className="mt-1 text-xs text-text-muted">The key cannot be changed after creation.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Optional description"
            className={`${inputClass} resize-y`}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-border accent-primary"
          />
          Active
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending || !name.trim() || !key}
            className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? 'Saving...' : isEdit ? 'Save changes' : `Create ${entityLabel}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
